import React, { useState } from 'react';

type AlertLevel = 'amber' | 'weather' | 'civil' | 'custom';

interface BroadcastPayload {
  level: AlertLevel;
  title: string;
  message: string;
  deviceIds: string[];
  durationMinutes: number;
}

const ALERT_LEVELS: { value: AlertLevel; label: string; color: string }[] = [
  { value: 'amber', label: 'AMBER Alert', color: 'bg-amber-500' },
  { value: 'weather', label: 'Severe Weather', color: 'bg-red-600' },
  { value: 'civil', label: 'Civil Emergency', color: 'bg-orange-600' },
  { value: 'custom', label: 'Custom Alert', color: 'bg-blue-600' },
];

const DURATION_OPTIONS = [5, 15, 30, 60, 120];

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const EmergencyBroadcastPanel: React.FC = () => {
  const [level, setLevel] = useState<AlertLevel>('amber');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [targetAll, setTargetAll] = useState(true);
  const [deviceFilter, setDeviceFilter] = useState('');
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [activeBroadcast, setActiveBroadcast] = useState<BroadcastPayload | null>(null);

  const selectedLevel = ALERT_LEVELS.find((l) => l.value === level) ?? ALERT_LEVELS[0];

  const handleSend = async () => {
    if (!confirm) {
      setConfirm(true);
      return;
    }

    setSending(true);
    setResult(null);

    const deviceIds = targetAll
      ? ['*']
      : deviceFilter
          .split(',')
          .map((d) => d.trim())
          .filter(Boolean);

    const payload: BroadcastPayload = {
      level,
      title: title.trim(),
      message: message.trim(),
      deviceIds,
      durationMinutes,
    };

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/emergency-broadcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        setResult({
          ok: true,
          text: `Broadcast sent to ${data.deviceCount ?? 'all'} device(s).`,
        });
        setActiveBroadcast(payload);
        setConfirm(false);
      } else {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        setResult({ ok: false, text: err.message || 'Broadcast failed' });
        setConfirm(false);
      }
    } catch {
      setResult({ ok: false, text: 'Network error — could not reach server.' });
      setConfirm(false);
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/api/admin/emergency-broadcast`, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      setActiveBroadcast(null);
      setResult({ ok: true, text: 'Emergency broadcast cancelled.' });
    } catch {
      setResult({ ok: false, text: 'Failed to cancel broadcast.' });
    } finally {
      setSending(false);
    }
  };

  const canSend = title.trim().length > 0 && message.trim().length > 0;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <span className="text-2xl">🚨</span>
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Emergency Broadcast</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Push critical alerts to all or selected billboard devices
          </p>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Active broadcast banner */}
        {activeBroadcast && (
          <div className="flex items-center justify-between rounded-lg bg-red-50 border border-red-200 p-4">
            <div>
              <p className="text-sm font-semibold text-red-800">Active: {activeBroadcast.title}</p>
              <p className="text-xs text-red-600">
                {activeBroadcast.level.toUpperCase()} · {activeBroadcast.durationMinutes} min
              </p>
            </div>
            <button
              onClick={handleCancel}
              disabled={sending}
              className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              Cancel Broadcast
            </button>
          </div>
        )}

        {/* Alert level selector */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Alert Level</label>
          <div className="flex flex-wrap gap-2">
            {ALERT_LEVELS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  setLevel(opt.value);
                  setConfirm(false);
                }}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition ${
                  level === opt.value
                    ? `${opt.color} text-white`
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div>
          <label htmlFor="eb-title" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Alert Title
          </label>
          <input
            id="eb-title"
            type="text"
            maxLength={120}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setConfirm(false);
            }}
            placeholder="e.g. AMBER Alert — Missing Child"
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="eb-message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Message
          </label>
          <textarea
            id="eb-message"
            rows={3}
            maxLength={500}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setConfirm(false);
            }}
            placeholder="Detailed alert message to display on billboard screens…"
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-slate-400">{message.length}/500</p>
        </div>

        {/* Duration */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Duration (minutes)
          </label>
          <div className="flex flex-wrap gap-2">
            {DURATION_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => setDurationMinutes(d)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  durationMinutes === d
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                }`}
              >
                {d} min
              </button>
            ))}
          </div>
        </div>

        {/* Target devices */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Target Devices</label>
          <div className="flex items-center gap-3 mb-2">
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <input
                type="radio"
                name="target"
                checked={targetAll}
                onChange={() => setTargetAll(true)}
                className="accent-blue-600"
              />
              All devices
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <input
                type="radio"
                name="target"
                checked={!targetAll}
                onChange={() => setTargetAll(false)}
                className="accent-blue-600"
              />
              Specific devices
            </label>
          </div>
          {!targetAll && (
            <input
              type="text"
              value={deviceFilter}
              onChange={(e) => setDeviceFilter(e.target.value)}
              placeholder="Comma-separated device IDs"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          )}
        </div>

        {/* Result message */}
        {result && (
          <div
            className={`rounded-lg p-3 text-sm ${
              result.ok
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {result.text}
          </div>
        )}

        {/* Send / Confirm */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSend}
            disabled={!canSend || sending}
            className={`rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition disabled:opacity-50 ${
              confirm ? 'bg-red-600 hover:bg-red-700' : `${selectedLevel.color} hover:opacity-90`
            }`}
          >
            {sending ? 'Sending…' : confirm ? 'Confirm — Send Now' : 'Send Emergency Broadcast'}
          </button>
          {confirm && (
            <button
              onClick={() => setConfirm(false)}
              className="rounded-lg border border-slate-300 dark:border-slate-600 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmergencyBroadcastPanel;
